// Small shared pieces used by the signed-out pages and a few panels.
//
// Rewritten in phase 7 against the ported design system (src/index.css). The
// signed-out screens get real treatment rather than riding the transition
// layer, because they are the first thing anyone ever sees of Dayboard.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BrandLockup } from './components/BrandMark';

/**
 * The signed-out card: sign in, sign up, reset a password, accept an invite.
 *
 * Carries `legacy-page` because the forms inside it are still written against
 * bare <label>/<input>/<button> markup. The card itself is not — it is styled
 * here directly, so the one screen every customer sees first is not waiting on
 * a transition layer to look finished.
 */
export function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="legacy-page flex min-h-screen-safe items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 rounded no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
          >
            <BrandLockup markClass="h-9 w-9" textClass="text-2xl" />
          </Link>
          <p className="mt-2 text-sm text-gray-600">
            Your crew opens the app and sees where they are going.
          </p>
        </div>

        <div className="card p-7">
          <h1 className="!mb-5 !text-lg">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Banner({ kind, children }: { kind: 'error' | 'ok'; children: ReactNode }) {
  return (
    <p
      className={`${kind === 'error' ? 'banner-error' : 'banner-ok'} mb-4`}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {children}
    </p>
  );
}

export function Field({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  autoComplete,
  required = true,
  enterKeyHint,
  autoCapitalize,
  spellCheck,
  inputMode,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  autoComplete?: string;
  required?: boolean;
  /** Labels the phone keyboard's return key — "go" on the field that submits. */
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  /** Pass "none" on identifier-like fields so a phone keyboard does not
      capitalise or "correct" them. */
  autoCapitalize?: string;
  spellCheck?: boolean;
  /** Which on-screen keyboard a phone offers — "numeric" for an ABN. */
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'decimal' | 'none';
}) {
  return (
    <label htmlFor={name}>
      {label}
      {/* On the LABEL line, not under the input. A `.row` aligns its fields
          along the bottom edge, so an error appearing under one input used to
          shove that input up out of line with its neighbours; above the input,
          any extra height grows upward and the bottom edge never moves. */}
      {error && (
        <span className="field-error-inline" id={`${name}-error`}>
          {error}
        </span>
      )}
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        enterKeyHint={enterKeyHint}
        autoCapitalize={autoCapitalize}
        spellCheck={spellCheck}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint && !error && (
        <span className="hint" id={`${name}-hint`}>
          {hint}
        </span>
      )}
    </label>
  );
}

/**
 * A password input with a Show/Hide toggle (Nathan, launch day 2026-09-14:
 * signing up, there was no way to see the password you had typed).
 *
 * Two styling constraints that look odd until you know the bare-element rules
 * in legacy.css: the INPUT must stay classless (its whole look comes from
 * `input:not([class])`, so the room for the button is inline style, not a
 * padding utility), and the BUTTON must NOT be classless (`button:not([class])`
 * would dress it as a full-size submit). The toggle is drawn small and touched
 * at 44px by the sanctioned `after:-inset` trick — one per field, so two can
 * never overlap.
 *
 * Revealing resets on submit-ish events deliberately not at all: the person
 * chose to look, and flipping it back mid-typo is the annoyance, not the fix.
 */
export function PasswordField({
  label,
  name,
  value,
  onChange,
  error,
  hint,
  autoComplete = 'new-password',
  enterKeyHint,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  autoComplete?: string;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
}) {
  const [shown, setShown] = useState(false);
  return (
    <label htmlFor={name}>
      {label}
      {error && (
        <span className="field-error-inline" id={`${name}-error`}>
          {error}
        </span>
      )}
      <span className="relative block">
        <input
          id={name}
          name={name}
          type={shown ? 'text' : 'password'}
          value={value}
          required
          autoComplete={autoComplete}
          enterKeyHint={enterKeyHint}
          autoCapitalize="none"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
          style={{ paddingRight: '3.25rem' }}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 py-0.5 text-sm font-medium text-gray-600 hover:text-gray-900 after:absolute after:-inset-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          aria-pressed={shown}
          onClick={() => setShown((current) => !current)}
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </span>
      {hint && !error && (
        <span className="hint" id={`${name}-hint`}>
          {hint}
        </span>
      )}
    </label>
  );
}
